import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Container,
    CircularProgress,
    IconButton,
    AppBar,
    Toolbar,
    Link,
    Avatar,
    Dialog,
    DialogContent,
    Slide,
    Stack,
    Button,
    Divider,
    Grid
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { useAuth } from '../auth/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import GroupLocation from './GroupLocation';
import SVGComponent from './svgviewer-react-output-remaster';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const GroupPublicPage = () => {
    const { dbUser, isLoading: authLoading } = useAuth();
    const { groupName: encodedGroupName } = useParams();
    const navigate = useNavigate();

    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openImageDialog, setOpenImageDialog] = useState(false);

    const decodedGroupName = encodedGroupName ? decodeURIComponent(encodedGroupName).replace(/-/g, ' ') : '';

    const fetchGroup = useCallback(async () => {
        if (!dbUser || !decodedGroupName) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const fetchedGroup = await dbUser.getGroupByName(decodedGroupName);
            if (fetchedGroup) {
                setGroup(fetchedGroup);
            } else {
                setError(`Grupo "${decodedGroupName}" no encontrado.`);
            }
        } catch (err) {
            console.error('Failed to fetch group:', err);
            setError('Error al cargar la información del grupo.');
        } finally {
            setLoading(false);
        }
    }, [dbUser, decodedGroupName]);

    useEffect(() => {
        if (!authLoading) {
            fetchGroup();
        }
    }, [authLoading, fetchGroup]);

    if (loading || authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ marginLeft: 2 }}>Cargando página del grupo...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mt: 8 }}>
                <Typography variant="h5" color="error">{error}</Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Asegúrate de que la URL es correcta o <Link onClick={() => navigate('/misbolos/groups')} sx={{ cursor: 'pointer' }}>explora otros grupos</Link>.
                </Typography>
            </Box>
        );
    }

    if (!group) return null;

    return (
        <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }} id="box1">
            {/* HERO IMAGE */}
            {group.images?.[0] && (
                <Box sx={{ position: 'relative', height: { xs: 300, md: 450 }, mb: 4 }} id="imgbox">
                    <img
                        id="pureimg"
                        src={`https://bluejims.com:5005/misbolos/image/${group.images[0]}`}
                        alt={group.name}
                        onClick={() => setOpenImageDialog(true)}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            filter: 'brightness(0.6)',
                            cursor: 'pointer',
                        }}
                    />
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '-webkit-fill-available',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        textAlign: 'center',
                        px: 2,
                    }}>
                        <Typography variant="h2" sx={{ fontWeight: 700, textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
                            {group.name}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Container maxWidth="md" sx={{ backgroundColor: '#fff', py: 6, borderRadius: 3, boxShadow: 1 }}>
                {/* LOCATION */}
                {group.location && (
                    <Box sx={{ mb: 2 }}>
                        <GroupLocation location={group.location} />
                    </Box>
                )}

                {/* DESCRIPTION */}
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                    Sobre el grupo
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                    {group.description}
                </Typography>

                {group.images?.slice(1).length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <Grid container spacing={2}>
                            {group.images.slice(1).map((img, idx) => (
                                <Grid item xs={12} sm={6} key={idx}>
                                    <Box
                                        sx={{
                                            overflow: 'hidden',
                                            borderRadius: 2,
                                            boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
                                            transition: 'transform 0.3s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <img
                                            src={`https://bluejims.com:5005/misbolos/image/${img}`}
                                            alt={`Imagen ${idx + 2}`}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block'
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}


                {/* SERVICES */}
                {group.services && (
                    <>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4, mb: 1 }}>
                            Servicios
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {group.services}
                        </Typography>
                    </>
                )}

                {/* MEMBERS */}
                {group.members?.length > 0 && (
                    <>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4, mb: 2 }}>
                            Integrantes
                        </Typography>
                        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                            {group.members.map((member, index) => {
                                const imageUrl = `https://bluejims.com:5005/misbolos/image/${member.profilePicture || member.photoURL}/q/5`;
                                return (
                                    <Box key={index} sx={{ textAlign: 'center' }}>
                                        <Avatar
                                            src={imageUrl}
                                            alt={member.displayName || member.email}
                                            sx={{ width: 64, height: 64, margin: 'auto' }}
                                        />
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                            {member.displayName || member.email}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}

                {/* SOCIAL LINKS */}
                {(group.links && Object.values(group.links).some(Boolean)) && (
                    <>
                        <Divider sx={{ my: 4 }} />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Encuéntranos en
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <IconButton component="a" href={`/misbolos/groups/${encodeURIComponent(group.name)}`} sx={{ color: '#000' }}>
                                <SVGComponent style={{ width: 32, height: 32 }} />
                            </IconButton>
                            {group.links.website && (
                                <IconButton component="a" href={group.links.website} target="_blank" sx={{ color: '#000' }}>
                                    <LanguageIcon fontSize="large" />
                                </IconButton>
                            )}
                            {group.links.facebook && (
                                <IconButton component="a" href={group.links.facebook} target="_blank" sx={{ color: '#1877f2' }}>
                                    <FacebookIcon fontSize="large" />
                                </IconButton>
                            )}
                            {group.links.instagram && (
                                <IconButton component="a" href={group.links.instagram} target="_blank" sx={{ color: '#e4405f' }}>
                                    <InstagramIcon fontSize="large" />
                                </IconButton>
                            )}
                            {group.links.youtube && (
                                <IconButton component="a" href={group.links.youtube} target="_blank" sx={{ color: '#ff0000' }}>
                                    <YouTubeIcon fontSize="large" />
                                </IconButton>
                            )}
                            {group.links.bandcamp && (
                                <Button href={group.links.bandcamp} target="_blank" variant="outlined" size="small">
                                    Bandcamp
                                </Button>
                            )}
                        </Stack>
                    </>
                )}

                {/* ACTIVITIES */}
                {group.activities?.length > 0 && (
                    <>
                        <Divider sx={{ my: 4 }} />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Próximas Actividades
                        </Typography>
                        <Stack spacing={1}>
                            {group.activities.map((activity, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        padding: 2,
                                        backgroundColor: '#f0f4ff',
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: '#e3edff' }
                                    }}
                                    onClick={() => navigate(`/misbolos/activities/${activity.title}`)}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {activity.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {activity.detailedInfo}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {new Date(activity.date).toLocaleDateString('es-ES')}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </>
                )}
            </Container>

            {/* FULLSCREEN IMAGE VIEW */}
            {group.images?.length > 0 && (
                <Dialog
                    fullScreen
                    open={openImageDialog}
                    onClose={() => setOpenImageDialog(false)}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: 'relative', background: 'black' }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={() => setOpenImageDialog(false)}>
                                <ArrowBackIosNewIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
                                Imagen de {group.name}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <DialogContent sx={{
                        backgroundColor: 'black',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 0,
                    }}>
                        <img
                            src={`https://bluejims.com:5005/misbolos/image/${group.images[0]}`}
                            alt={group.name}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

export default GroupPublicPage;
